import { TextareaAutosize } from '@mui/base/TextareaAutosize';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { each, filter, noop } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import * as commentUtilities from '../../stores/cohort/utils';
import IwButton from '../app/IwButton';
import MaterialAvatar from '../general/MaterialAvatar';
import OverlayTitle from '../generics/components/overlay-title';
import CanvasEntryComment from "./CanvasEntryComment";
// Color basis for this component
const GOOGLE_SPACE_BORDER_COLOR = '#DDDDDD';
const GOOGLE_FOCUS_BORDER_COLOR = '#C8C8C8';
const GOOGLE_PRIMARY_FONT_COLOR = '#333333';
const GOOGLE_SECONDARY_FONT_COLOR = '#777777';

const materialTextareaStyle = {
    borderRadius: '4px',
    border: '1px solid #dcdcdc',
    padding: '10px',
    resize: 'none'
}

const avatarStyle = {
    display: 'inline-block',
    float: 'left',
    width: '35px',
    marginTop: '5px'
};

const userNameStyle = {
    color: GOOGLE_PRIMARY_FONT_COLOR, display: 'block',
    float: 'left',
    fontSize: '14px',
    marginLeft: '3px',
    marginRight: '110px'
};

const commentDateStyle = {
    color: GOOGLE_SECONDARY_FONT_COLOR, display: 'block',
    float: 'left',
    fontSize: '12px',
    marginLeft: '3px',
    marginRight: '110px'
};

const NEW_COMMENT_ADDITIONAL_STYLE = {
    display: 'block',
    marginLeft: '10px',
    paddingBottom: '57px',
    width: '100%'
};

interface IProps {
    org: OrgState;
    cohort: CohortState;
    user: User;
    team: TeamState;
    entryID: string;
    entryContent: string;
    entrySegment: string;
    targetCommentID: string;
    closeCommentView: Function;
    queryClient: QueryClient;
    updateComment: any;

    // Refer to comment in the canvas prop of the same name
    comments: api.Comment[];
    commentContextID: string;
    authToken: string;
    theme?: Theme;
}

interface IState {
    editingComment: string;
    commentCollapseSet: { [key: string]: boolean };
    clickAwayLocked: boolean;
    savingComment: boolean;
    newCommentText: string;
    seenIDSet: string[];
}

interface Refs {
    [key: string]: React.ReactInstance;
}

export class CanvasCommentsDialog extends React.Component<IProps, IState> {
    static defaultProps = {
        title: "",
    };

    refs: Refs;
    state: IState = {
        editingComment: "",
        commentCollapseSet: {},
        clickAwayLocked: false,
        savingComment: false,
        newCommentText: "",
        seenIDSet: [],
    };
    rootNode: HTMLDivElement;
    scrollNode: HTMLDivElement;
    newCommentArea: HTMLTextAreaElement;

    commentListRef: React.RefObject<HTMLDivElement> = React.createRef();

    componentDidMount() {
        this.handleMarkingCommentsAndNotificationsAsRead();
    }

    componentDidUpdate(prevProps: IProps) {
        if (this.props?.comments !== prevProps.comments) {
            this.commentListRef.current.scroll(
                0,
                this.commentListRef.current.scrollHeight
            );
        }
    }

    getNewCommentValue = () => {
        const { newCommentText } = this.state;

        return newCommentText;
    };

    scrollToCommentID(targetCommentID) {
        const { comments, entryID } = this.props;

        // If no target was given, find the "last" comment
        if (!targetCommentID) {
            let allComments = commentUtilities.searchComments(
                "bmc-comment",
                undefined,
                undefined,
                entryID,
                comments
            );

            if (allComments?.length === 0) return; // If no comments exist don't bother

            targetCommentID = allComments?.[allComments?.length - 1]?.id;
        }

        // If we mount with a given comment target, try to find it and scroll to it
        const commentNode = (
            typeof window !== "undefined" ? document : {}
        ).getElementById("cmt-" + targetCommentID);

        if (commentNode) {
            // Sticky our popout to the comment location
            this.scrollNode.scrollTop =
                this.scrollNode.scrollTop + commentNode.offsetTop;
        }
    }

    handleMarkingCommentsAndNotificationsAsRead = () => {
        const { seenIDSet } = this.state;

        const { user, authToken, cohort } = this.props;

        const comments = this.getAllComments();

        // Only look at "unseen" comments
        let unseenComments = filter(comments, (comment) => {
            return !comment?.isSeen && comment?.userID !== user?.id;
        });

        if (unseenComments?.length > 0) {
            axios
                .put(
                    `${process.env.NEXT_PUBLIC_LINX_URL}/seen`,
                    {
                        type: "comment",
                        cohortID: cohort?.id,
                        targets: unseenComments.map(
                            (comment) => comment?.commentID
                        ),
                    },
                    {
                        withCredentials: false,
                        headers: {
                            Authorization: "Bearer " + authToken,
                        },
                    }
                ).then((res) => {
                    // console.log("comments seen", res);
                    this.props.queryClient.invalidateQueries({queryKey: ["comments"]});
                }).catch(error => {
                    console.error("Couldn't mark comments as seen.");
                })
        }
    };

    // Utility to handle searching through the bmc comments
    getAllComments = () => {
        const { comments, entryID } = this.props;

        let allFoundComments = commentUtilities.searchComments(
            "bmc-comment",
            undefined,
            undefined,
            entryID,
            comments
        );

        return allFoundComments;
    };

    // Check for all the situations that would result
    // in not having any available comments
    // to show
    isFreshDialog = () => {
        const comments = this.getAllComments();

        if (comments === undefined) return false;

        if (comments?.length !== 0) return false;

        // There are comments and the comment data has been loaded
        return true;
    };

    // This is the inverse of isCommentWrittenYet
    // so we can drop it directly into a "disabled" prop
    isNotCommentWrittenYet = () => {
        const commentValue = this.state.editingComment;

        if (commentValue?.length <= 0) {
            return true;
        }

        return false;
    };

    handleProcessEscaping = () => {
        const { clickAwayLocked } = this.state;

        if (clickAwayLocked) return;

        if (this.state.editingComment === "") {
            this.props.closeCommentView();
        }
    };

    handleKeyPress = (event) => {
        // Since this is by default a multi-line HTML component we need to block enter's native functionality
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
            if (this.state.savingComment === false) {
                this.handlePostComment();
                event.preventDefault();
            }
        }
    };

    handleEscapeCheck = (event) => {
        if (event.key) {
            if (event.key === "Escape") {
                this.handleProcessEscaping();
            }
        }

        if (event.target) {
            if (this.rootNode) {
                if (!this.rootNode.contains(event.target)) {
                    // The root node didn't contain the target click on the document
                    this.handleProcessEscaping();
                }
            }
        }
    };

    handleUpdateComment = ({
        commentID,
        commentContent,
        onSuccess = noop,
        onFailure = noop,
    }) => {
        const { user, team, entryID, commentContextID } = this.props;

        this.props.updateComment({
            payload: {
                cohortID: commentContextID,
                userID: user?.id,
                slug: "bmc-comment",
                rating: entryID,
                iterator: 0,
                groupID: team?.id,
                content: commentContent,
                commentID: commentID,
                isSeen: undefined,
            },
            commentContextID,
            entryID,
            onSuccess,
            onFailure,
        });
    };

    handlePostComment = () => {
        const { commentContextID, team, entryID, user } = this.props;

        // Attempt to find the user given contents
        // of our new comment
        let commentContent = this.state.editingComment;

        // Check for empty comments, then ignore the request
        if (
            commentContent === "" ||
            commentContent === undefined ||
            commentContent === null
        ) {
            return;
        }

        this.setState({
            savingComment: true,
        });

        this.props.updateComment({
            payload: {
                cohortID: commentContextID,
                userID: user?.id,
                slug: "bmc-comment",
                rating: entryID,
                iterator: 0,
                groupID: team?.id,
                content: this.state.editingComment,
                commentID: undefined,
                isSeen: undefined,
            },
            commentContextID,
            entryID,
            onSuccess: () => {
                this.setState({
                    savingComment: false,
                    editingComment: "",
                });
            },
        });
    };

    renderCommentHeader({
        name,
        avatarSource,
        date,
        avatarSize = 32,
        commentLink = null,
    }) {
        return (
            <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ paddingBottom: "5px" }}
                spacing={2}
            >
                <Tooltip title={name}>
                    <MaterialAvatar
                        sx={{
                            width: avatarSize,
                            height: avatarSize,
                        }}
                        user={{
                            name,
                            photoURL: avatarSource,
                        }}
                    />
                </Tooltip>
                <Stack
                    direction="column"
                    flexGrow={1}
                >
                    <Typography>{name}</Typography>
                    <Typography
                        color="textSecondary"
                        variant="body2"
                    >
                        {moment(date).format("MMM DD 'YY")}
                    </Typography>
                </Stack>
            </Stack>
        );
    }

    renderSaveButton = () => {
        const { savingComment } = this.state;

        const buttonDisabled = savingComment || this.isNotCommentWrittenYet();

        return (
            <IwButton
                disabled={buttonDisabled}
                variant="outlined"
                loading={savingComment}
                onClick={this.handlePostComment}
            >
                Comment
            </IwButton>
        );
    };

    render() {
        const { org, team, cohort, user, theme } = this.props;

        const comments = this.props?.comments;

        let commentSegments = [];

        each(comments, (comment,index) => {
            commentSegments.push(
                <CanvasEntryComment
                    key={comment.id || index}
                    org={org}
                    team={team}
                    cohort={cohort}
                    entryID={this.props?.entryID}
                    user={user}
                    comment={comment}
                    handleUpdateComment={this.handleUpdateComment}
                />
            );
        });

        let additionalNewComment = <span></span>;

        if (user) {
            additionalNewComment = (
                <span style={NEW_COMMENT_ADDITIONAL_STYLE}>
                    <span style={avatarStyle}>
                        {commentUtilities.findUserAvatar(user?.id, org, 27)}
                    </span>
                    <span style={userNameStyle}>
                        {commentUtilities.findUserName(user?.id, org)}
                    </span>
                    <span style={commentDateStyle}>
                        {commentUtilities.formatSingleDate(moment().toString())}
                    </span>
                </span>
            );
        }

        // Assume normal height percentages
        let firstPanelHeight = "76%";
        let lastPanelHeight = "100%";
        let rootPanelHeight = "350px";

        let commentHeader = undefined;

        // However if this entry has no other comments then show a special
        // "New Comment" view
        if (comments?.length === 0) {
            firstPanelHeight = "0%";
            lastPanelHeight = "100%";
            commentHeader = this.renderCommentHeader({
                name: this.props?.user?.name,
                avatarSource: this.props?.user?.photoURL,
                date: new Date(),
            });
            rootPanelHeight = "155px";
        } else {
            additionalNewComment = undefined;
        }

        let newCommentField = (
            <Stack spacing={0}>
                {commentHeader}
                <TextareaAutosize
                    minRows={1}
                    maxLength={1000}
                    maxRows={15}
                    style={materialTextareaStyle}
                    value={this.state.editingComment}
                    onChange={(event) => {
                        this.setState({
                            editingComment: event.target.value,
                        });
                    }}
                />
                <Stack
                    sx={{ marginTop: "10px" }}
                    direction="row"
                >
                    {this.renderSaveButton()}
                    <Button
                        variant="outlined"
                        sx={{ marginLeft: "10px" }}
                        onClick={() => {
                            this.props.closeCommentView();
                        }}
                    >
                        {this.isNotCommentWrittenYet() ? "Close" : "Cancel"}
                    </Button>
                </Stack>
            </Stack>
        );

        return (
            <Stack
                className="comment-popover-content"
                sx={{
                    background: "white",
                    border: "0px",
                    borderRadius: "4px",
                    boxShadow: theme?.custom?.menuBoxShadow,
                    padding: "0px",
                    margin: "0px",
                }}
                spacing={0}
            >
                <OverlayTitle
                    title="Comments"
                    onClose={this.props?.closeCommentView}
                />
                <Box
                    ref={this.commentListRef}
                    sx={{
                        borderLeft: "1px solid #dcdcdc",
                        borderRight: "1px solid #dcdcdc",
                        maxHeight: "300px",
                        overflowY: "scroll",
                        overflowX: "hidden",
                        scrollPadding: "0px",
                        paddingLeft: "10px",
                        scrollBehavior: "smooth",
                    }}
                >
                    {commentSegments}
                </Box>
                <Box
                    sx={{
                        padding: "10px",
                        borderBottomLeftRadius: "4px",
                        borderBottomRightRadius: "4px",
                        borderLeft: "1px solid #dcdcdc",
                        borderRight: "1px solid #dcdcdc",
                        borderBottom: "1px solid #dcdcdc",
                    }}
                >
                    {newCommentField}
                </Box>
            </Stack>
        );
    }
}
